import { useEffect, useMemo, useState } from 'react';
import {
  Box, Button, Card, Checkbox, Chip, CircularProgress, MenuItem, Stack, Table, TableBody,
  TableCell, TableHead, TableRow, TableSortLabel, TextField, Tooltip, Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { api } from '../../services/api';
import type { EmballageProduct, EmballageFraktionDef, EmballageFraktion } from '../../types';
import { useSort, type SortValue } from './sorting';

interface Props {
  onNotify: (msg: string, error?: boolean) => void;
  onChanged: () => void;
}

export default function EmballageProductsPanel({ onNotify, onChanged }: Props) {
  const [products, setProducts] = useState<EmballageProduct[]>([]);
  const [fraktioner, setFraktioner] = useState<EmballageFraktionDef[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [search, setSearch] = useState('');
  const [onlyRelevant, setOnlyRelevant] = useState(true);

  const fraktionLabel = useMemo(
    () => new Map(fraktioner.map(f => [f.fraction, f.label])),
    [fraktioner],
  );

  const load = async () => {
    setLoading(true);
    try {
      const [p, f] = await Promise.all([api.getEmballageProducts(), api.getEmballageFraktioner()]);
      setProducts(p);
      setFraktioner(f);
    } catch {
      onNotify('Varerne kunne ikke hentes', true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const r = await api.syncEmballageProducts();
      onNotify(`${r.created} nye varer, ${r.autoMapped} genkendt på E-kode`);
      await load();
      onChanged();
    } catch {
      onNotify('Synkroniseringen mod DORA fejlede', true);
    } finally {
      setSyncing(false);
    }
  };

  const patch = async (p: EmballageProduct, data: { fraction?: EmballageFraktion | null; included?: boolean }) => {
    const before = p;
    setProducts(prev => prev.map(x => (x.id === p.id ? { ...x, ...data, autoMapped: false } : x)));
    try {
      const updated = await api.updateEmballageProduct(p.id, data);
      setProducts(prev => prev.map(x => (x.id === p.id ? updated : x)));
      onChanged();
    } catch {
      setProducts(prev => prev.map(x => (x.id === p.id ? before : x)));
      onNotify('Ændringen blev ikke gemt', true);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return products.filter(p => {
      // Kataloget er stort, så listen viser som udgangspunkt kun de varer der
      // er mappet eller foreslået. Søgning slår filtret fra.
      if (onlyRelevant && !q && !p.fraction && !p.included) return false;
      if (!q) return true;
      return p.doraProductName.toLowerCase().includes(q) || p.doraProductNumber.includes(q);
    });
  }, [products, search, onlyRelevant]);

  const sortValue = (p: EmballageProduct, key: string): SortValue => {
    switch (key) {
      case 'included': return p.included;
      case 'number': return p.doraProductNumber;
      case 'name': return p.doraProductName;
      case 'eKode': return p.eKode;
      case 'fraction': return p.fraction ? fraktionLabel.get(p.fraction) ?? p.fraction : null;
      default: return p.doraProductName;
    }
  };
  const { key, dir, toggle, sorted } = useSort(filtered, sortValue, 'name');

  const includedCount = products.filter(p => p.included).length;

  const header = (id: string, label: string, align: 'left' | 'center' = 'left') => (
    <TableCell align={align} sortDirection={key === id ? dir : false}>
      <TableSortLabel active={key === id} direction={key === id ? dir : 'asc'} onClick={() => toggle(id)}>
        {label}
      </TableSortLabel>
    </TableCell>
  );

  return (
    <Box>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <TextField
          size="small"
          placeholder="Søg i varekataloget"
          value={search}
          onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.disabled' }} /> }}
          sx={{ minWidth: 260 }}
        />
        <Button size="small" variant={onlyRelevant ? 'contained' : 'outlined'} onClick={() => setOnlyRelevant(v => !v)}>
          Kun affaldsvarer ({includedCount} med)
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button size="small" startIcon={<RefreshIcon />} onClick={handleSync} disabled={syncing}>
          {syncing ? 'Henter' : 'Hent fra DORA'}
        </Button>
      </Stack>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}><CircularProgress /></Box>
      ) : products.length === 0 ? (
        <Card variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">Ingen varer hentet endnu.</Typography>
        </Card>
      ) : (
        <Card variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                {header('included', 'Med', 'center')}
                {header('number', 'Varenr.')}
                {header('name', 'Vare')}
                {header('eKode', 'E-kode')}
                {header('fraction', 'Fraktion')}
              </TableRow>
            </TableHead>
            <TableBody>
              {sorted.map(p => (
                <TableRow key={p.id} hover selected={p.included}>
                  <TableCell align="center" padding="checkbox">
                    <Tooltip title={!p.fraction && !p.included ? 'Vælg en fraktion først' : ''}>
                      <span>
                        <Checkbox
                          size="small"
                          checked={p.included}
                          disabled={!p.fraction && !p.included}
                          onChange={() => patch(p, { included: !p.included })}
                        />
                      </span>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>{p.doraProductNumber}</TableCell>
                  <TableCell>{p.doraProductName}</TableCell>
                  <TableCell>
                    {p.eKode ? (
                      <Chip
                        size="small"
                        label={p.eKode}
                        variant="outlined"
                        icon={p.autoMapped ? <AutoAwesomeIcon sx={{ fontSize: 14 }} /> : undefined}
                      />
                    ) : ''}
                  </TableCell>
                  <TableCell>
                    <TextField
                      select size="small" variant="standard" sx={{ minWidth: 180 }}
                      value={p.fraction ?? ''}
                      onChange={e => patch(p, { fraction: (e.target.value || null) as EmballageFraktion | null })}
                    >
                      <MenuItem value="">Ingen</MenuItem>
                      {fraktioner.map(f => (
                        <MenuItem key={f.fraction} value={f.fraction}>{f.label} ({f.eKode})</MenuItem>
                      ))}
                    </TextField>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </Box>
  );
}
